import React from 'react';
import { Button } from '../ui/button';
import { getFileIcon, formatDate } from '../../lib/libraryUtils';

/**
 * Trash view component with selection and permanent delete
 */
const TrashView = ({
  folders,
  documents,
  summary,
  loading,
  selectedItems,
  deletingItems,
  onToggleSelection,
  onToggleSelectAll,
  onRestoreFolder,
  onRestoreDocument,
  onPermanentDelete,
  onRetryDelete,
  onBulkDelete,
  onEmptyTrash,
}) => {
  const isAllSelected = selectedItems.size === documents.length && documents.length > 0;
  const hasItems = folders.length > 0 || documents.length > 0;

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!hasItems) {
    return (
      <div className="text-center py-8 text-gray-400">
        <span className="text-4xl">🗑️</span>
        <p className="mt-2">Prullenbak is leeg</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary & Actions bar */}
      <div className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg">
        <span className="text-sm text-gray-600">
          {summary.total_items} item{summary.total_items !== 1 ? 's' : ''} in prullenbak
          {summary.failed_deletes > 0 && (
            <span className="ml-2 text-red-600">
              ({summary.failed_deletes} met fouten)
            </span>
          )}
        </span>
        <div className="flex items-center gap-2">
          {selectedItems.size > 0 && (
            <Button
              size="sm"
              variant="destructive"
              onClick={onBulkDelete}
              disabled={deletingItems}
              data-testid="btn-delete-selected"
            >
              🗑️ Verwijder geselecteerd ({selectedItems.size})
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={onEmptyTrash}
            disabled={deletingItems}
            data-testid="btn-empty-trash"
          >
            🧹 Prullenbak leegmaken
          </Button>
        </div>
      </div>

      {/* Folders section */}
      {folders.length > 0 && (
        <div>
          <h3 className="font-medium text-sm text-gray-600 mb-2">
            Mappen ({folders.length})
          </h3>
          <div className="space-y-2">
            {folders.map(folder => (
              <div 
                key={folder.id} 
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <span>📁</span>
                  <span className="text-sm">{folder.name}</span>
                  <span className="text-xs text-gray-400">
                    Verwijderd: {formatDate(folder.deleted_at)}
                  </span>
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => onRestoreFolder(folder)}
                >
                  ♻️ Herstel
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents section */}
      {documents.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-sm text-gray-600">
              Documenten ({documents.length})
            </h3>
            <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={() => onToggleSelectAll(documents.map(d => d.id))}
                className="rounded border-gray-300"
              />
              Selecteer alles
            </label>
          </div>
          <div className="space-y-2">
            {documents.map(doc => (
              <TrashDocumentRow
                key={doc.id}
                document={doc}
                isSelected={selectedItems.has(doc.id)}
                deletingItems={deletingItems}
                onToggleSelection={() => onToggleSelection(doc.id)}
                onRestore={() => onRestoreDocument(doc)}
                onPermanentDelete={() => onPermanentDelete(doc)}
                onRetryDelete={() => onRetryDelete(doc)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TrashDocumentRow = ({
  document,
  isSelected,
  deletingItems,
  onToggleSelection,
  onRestore,
  onPermanentDelete,
  onRetryDelete,
}) => {
  const isFailed = document.delete_status === 'delete_failed';

  return (
    <div 
      className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
        isSelected ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'
      } ${isFailed ? 'border-l-4 border-l-red-500' : ''}`}
      data-testid={`trash-doc-${document.id}`}
    >
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelection}
          className="rounded border-gray-300"
          data-testid={`checkbox-${document.id}`}
        />
        <span>{getFileIcon(document.extension)}</span>
        <div>
          <span className="text-sm">{document.title || document.original_name}</span>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>Verwijderd: {formatDate(document.deleted_at)}</span>
            {isFailed && (
              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                Verwijderen mislukt
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {isFailed ? (
          <Button 
            size="sm" 
            variant="outline"
            className="text-red-600"
            onClick={onRetryDelete}
            disabled={deletingItems}
          >
            🔄 Opnieuw
          </Button>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={onRestore}>
              ♻️ Herstel
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              className="text-red-600 hover:bg-red-50"
              onClick={onPermanentDelete}
              disabled={deletingItems}
              data-testid={`btn-permanent-delete-${document.id}`}
            >
              ✕
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default TrashView;
